import { useEffect, useState } from 'react'
import { ProfileProps } from '../../profile'
import useRequest from '@/shared/lib/use-request'
import dayjs, { Dayjs } from 'dayjs'
import { ConfigStore, LangStore } from '@/shared/model/store'
import { ILang } from '@/entities/feed-block/model/types'
import { IUser } from '@/entities/user/model/types'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
	Badge,
	Button,
	Card,
	Input,
	Preloader
} from '@/shared/ui'
import { PlsIcon, SavvaIcon } from '@/shared/assets'
import { DEFAULT_PREC, getUTCTimeToShowStrShortDate, shortNumberFormat } from '@/shared/lib'
import { ArrowDown, ArrowUp, CircleX, ListFilter } from 'lucide-react'
import { UserCard } from '@/entities/user'
import { OPTIONS_HISTORY } from './utils'
import { Link } from 'react-router-dom'
import { fixLengthPreview, getCurrentTitles } from '@/entities/feed-block/model/utils'
import FormatNumber from '@/shared/ui/format-number'
import CascadeFilter from './filter'
import { ContractsStore } from '@/shared/model/store/contracts-store'
import { null_address } from '@/shared/model/types'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'

interface ITransaction {
	contract: string
	domain: string
	type: string
	from: IUser
	to: IUser
	event_contract: string
	event_id: string
	amount: number
	savva_cid: string
	locales: ILang
	time_stamp: string
	token: string
	tx_hash: string
	info: string
	info_user: IUser
}

const system_user: IUser = {
	address: '0x0000000000000000000000000000000000000000' as `0x${string}`,
	name: 'firstvoice',
	avatar: 'system', // TODO SYSTEM AVATAR
	staked: 0n,
	n_following: 2,
	n_followers: 0,
	n_sponsoring: 0,
	n_sponsored: 0,
	n_nfts: 0,
	banned: false,
	ban_comment: '',
	profile_cid: '',
	display_name: 'System',
	chat_free_limit: 0,
	chat_price: 0,
	is_followed: false,
	is_following: false,
	sponsors_me_for: 0,
	total_sponsored: 0,
	total_sponsoring: 0,
	sendMessage: '',
	sponsor_values: [],
	nsfw: 'h',
	i_sponsor_for: 0
}

const DEFAULT_FILTERS = { contracts: '', type: '' }

type RangeValue = [Dayjs | null, Dayjs | null] | null

const RenderUser = ({ item, lang, profile }: { item: ITransaction; lang: ILang; profile: IUser }) => {
	const { contracts } = ContractsStore()
	const isSenderContract = Object.keys(contracts).find(k => contracts[k].address === item.from?.address)

	if (isSenderContract && isSenderContract !== null_address) {
		return (
			<div className='flex flex-row'>
				<UserCard
					user={{ ...system_user, display_name: isSenderContract }}
					descriprion={getUTCTimeToShowStrShortDate(item.time_stamp, lang)}
				/>
			</div>
		)
	}
	if (!item.to || !item.from) {
		return (
			<div className='flex flex-row'>
				<UserCard user={system_user} descriprion={getUTCTimeToShowStrShortDate(item.time_stamp, lang)} />
			</div>
		)
	}
	return (
		<div className='flex flex-row'>
			<UserCard
				user={item.from.address === profile.address ? item.to : item.from}
				descriprion={getUTCTimeToShowStrShortDate(item.time_stamp, lang)}
			/>
		</div>
	)
}

const RenderDescription = ({ item, langpack }: { item: ITransaction; langpack: (s: string) => void }) => {
	const hist = OPTIONS_HISTORY(langpack)
	const idx = hist.findIndex(i => i.value === item.contract)
	const event = hist[idx]?.children?.find(i => i.value === item.type)

	return (
		<div className='flex flex-col justify-center'>
			<span
				style={{ textAlign: 'start' }}
				className='text-[12px] text-[grey]'
			>{`${hist[idx]?.label}: ${event?.label}`}</span>
			{item.type === 'redeem' && item.contract === 'promo' && (
				<div className='text-[12px] text-[grey]'>{item.info}</div>
			)}
			{item.contract === 'club' && item.info && <UserCard user={item.info_user} className='h-5 w-5' />}
			{item.savva_cid && (
				<Link to={`/content/${item.savva_cid}`}>
					<div>{fixLengthPreview(getCurrentTitles(item.locales, 'title'), 30)}</div>
				</Link>
			)}
		</div>
	)
}

const RenderAmount = ({ obj, profile }: { obj: ITransaction; profile: IUser }) => {
	if (!obj.amount) return <></>
	if (obj.from?.address === profile?.address) {
		return (
			<div className='flex flex-row justify-end gap-4'>
				<span style={{ color: 'red' }}>
					-
					<FormatNumber number={obj.amount} />
				</span>

				{!obj.token ? <PlsIcon /> : <SavvaIcon />}
			</div>
		)
	}
	return (
		<div className='flex flex-row justify-end gap-4'>
			<span style={{ color: 'green' }}>
				+
				<FormatNumber number={obj.amount} />
			</span>
			{!obj.token ? <PlsIcon /> : <SavvaIcon />}
		</div>
	)
}

const TransactionHistory = ({ profile }: ProfileProps) => {
	const { lang, langPack } = LangStore()
	const { fetch: fetchHistory, isLoading } = useRequest('getHistory')
	const [transactionHistory, setTransactionHistory] = useState<ITransaction[]>([])
	const [rangeDate] = useState<RangeValue>([dayjs().add(-2, 'M'), dayjs()])
	const [savvaCid, setSavvaCid] = useState<string | undefined>(undefined)
	const [selectOptions, setSelectOptions] = useState(DEFAULT_FILTERS)
	const [selected, setSelected] = useState<string[]>([])
	const { domain } = ConfigStore()

	const totalSentSavva = transactionHistory.reduce((acc, item) => {
		if (item.from?.address === profile?.address && item.token) {
			return acc + Number(item.amount)
		}
		return acc
	}, 0)

	const totalGetSavva = transactionHistory.reduce((acc, item) => {
		if (item.to?.address === profile?.address && item.token) {
			return acc + Number(item.amount)
		}
		return acc
	}, 0)

	const totalGetPls = transactionHistory.reduce((acc, item) => {
		if (item.to?.address === profile?.address && !item.token) {
			return acc + Number(item.amount)
		}
		return acc
	}, 0)

	const totalSentPls = transactionHistory.reduce((acc, item) => {
		if (item.from?.address === profile?.address && !item.token) {
			return acc + Number(item.amount)
		}
		return acc
	}, 0)

	const getStories = () => {
		const startDate = rangeDate
			? dayjs(rangeDate[0]).add(1, 'day').startOf('day').subtract(1, 'second').format('YYYY-MM-DDTHH:mm:ss[Z]')
			: ''
		/* const endDate = rangeDate
			? dayjs(rangeDate[1]).add(1, 'day').startOf('day').subtract(1, 'second').format('YYYY-MM-DDTHH:mm:ss[Z]')
			: '' */

		fetchHistory({
			params: {
				domain: domain,
				user: profile.address,
				contracts: selectOptions.contracts,
				savva_cid: savvaCid,
				type: selectOptions.type,
				time_from: startDate
				// time_to: endDate
			},
			onSuccess(res) {
				setTransactionHistory(res)
			}
		})
	}

	useEffect(() => {
		if (!profile?.address) return
		getStories()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectOptions, savvaCid, rangeDate, profile.address])

	const enableFilters = !!Object.values(selectOptions).filter((el: string) => !!el).length || !!savvaCid

	return (
		<div>
			<div className='flex flex-row justify-between gap-10'>
				<Card className='flex w-[45%] flex-row items-center justify-between gap-10 px-6 py-4'>
					<SavvaIcon />
					<div className='flex flex-row items-center gap-2'>
						<ArrowDown size={16} color='green' />
						<div className='text-[24px] font-bold text-[green]'>{shortNumberFormat(totalGetSavva / DEFAULT_PREC)}</div>
					</div>
					<div className='flex flex-row items-center gap-2'>
						<ArrowUp size={16} color='red' />
						<div className='text-[24px] font-bold text-[red]'>{shortNumberFormat(totalSentSavva / DEFAULT_PREC)}</div>
					</div>
				</Card>
				<Card className='flex w-[45%] flex-row items-center justify-between gap-10 px-6 py-4'>
					<PlsIcon />
					<div className='flex flex-row items-center gap-2'>
						<ArrowDown size={16} color='green' />
						<div className='text-[24px] font-bold text-[green]'>{shortNumberFormat(totalGetPls / DEFAULT_PREC)}</div>
					</div>
					<div className='flex flex-row items-center gap-2'>
						<ArrowUp size={16} color='red' />
						<div className='text-[24px] font-bold text-[red]'>{shortNumberFormat(totalSentPls / DEFAULT_PREC)}</div>
					</div>
				</Card>
			</div>
			<div className='my-5 flex w-full flex-col'>
				<Accordion type='single' collapsible className='mb-2 w-full rounded-lg bg-background px-4'>
					<AccordionItem value='item-1'>
						<AccordionTrigger>{langPack('default.filters')}</AccordionTrigger>
						<AccordionContent>
							<Tabs defaultValue='market' className='w-full'>
								<div className='flex flex-row gap-4'>
									<TabsList className='grid w-full grid-cols-2'>
										<TabsTrigger value='market'>По типу</TabsTrigger>
										<TabsTrigger value='auction'>По ID поста</TabsTrigger>
									</TabsList>
									<Button
										size={'sm'}
										variant={'secondary'}
										disabled={!enableFilters}
										onClick={() => {
											setSelected([])
											setSavvaCid('')
										}}
									>
										{langPack('action.clear')}
									</Button>
								</div>

								<TabsContent value='market' className='p-2'>
									<CascadeFilter setSelectOptions={setSelectOptions} selected={selected} setSelected={setSelected} />
								</TabsContent>
								<TabsContent value='auction' className='p-2'>
									<div className='relative flex flex-row items-center'>
										<Input
											onChange={e => setSavvaCid(e.target.value)}
											value={savvaCid}
											placeholder={langPack('profile.history_input_placeholder_two')}
										/>
										<div className='absolute right-[10px] cursor-pointer' onClick={() => setSavvaCid('')}>
											<CircleX size={20} color='grey' />
										</div>
									</div>
								</TabsContent>
							</Tabs>
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			</div>
			<div className=''>
				{isLoading ? (
					<Preloader small />
				) : (
					transactionHistory.map((el: ITransaction) => (
						<div className='mb-6 grid grid-cols-6 grid-rows-1 gap-4 border-b-[1px] pb-6'>
							<div className='col-span-2'>
								<RenderUser item={el} profile={profile} lang={lang} />
							</div>
							<div className='col-span-2 col-start-3'>
								<RenderDescription item={el} langpack={langPack} />
							</div>
							<div className='col-start-5 flex flex-row items-center gap-4'>
								<a href={`https://scan.9inch.io/#/tx/${el.tx_hash}`} target='_blank'>
									<Badge className='rounded-sm'>TRX</Badge>
								</a>
								{el.savva_cid ? (
									<div onClick={() => setSavvaCid(el.savva_cid)} className='flex cursor-pointer flex-row items-center'>
										<ListFilter size={16} />
									</div>
								) : null}
							</div>
							<div className='col-start-6'>
								<RenderAmount obj={el} profile={profile} />
							</div>
						</div>
					))
				)}
			</div>
		</div>
	)
}

export default TransactionHistory
